import {
  SHTableBody,
  SHTableCell,
  SHTableRow,
} from "@components/design-systems";
import { FeatureAnalysisRowDTO } from "@models/platform-analysis/entities/steps/feature";
import { FeatureAnalysisRowLevel } from "@models/platform-analysis/enums/feature/featureAnalysisRowLevel";
import { RenderedProductRowTitleCell } from "@pages/platform-analysis-v2/_id/components/virtualized-table/components/rendered-items/row-title-cells/product";
import { Fragment, memo } from "react";
import { SummaryAnalysisTableDataValueCell } from "../../rendered-cells/data-value";
import { SummaryAnalysisTableTotalScoreCell } from "../../rendered-cells/total-score";
import { SummaryAnalysisTableTotalSelectedCell } from "../../rendered-cells/total-selected";

interface SummaryAnalysisTableBodyProps {
  rows: FeatureAnalysisRowDTO[];
  removeFeatureLevelRows?: boolean;
  removeSubSectionLevelRows?: boolean;
}

export const SummaryAnalysisTableBody = memo(
  ({
    rows,
    removeFeatureLevelRows = false,
    removeSubSectionLevelRows = false,
  }: SummaryAnalysisTableBodyProps) => {
    const renderTotalSelectionsCell = (
      row: FeatureAnalysisRowDTO,
      isFeatureLevel: boolean,
    ) => (
      <SHTableCell
        key={`TotalSelectionsCell-${row.id}`}
        className="SummaryAnalysisTableCell SummaryAnalysisTableCell-TotalSelections"
        sx={{ paddingX: "10px !important", paddingY: "0px !important" }}
      >
        {!isFeatureLevel && <SummaryAnalysisTableTotalSelectedCell {...row} />}
      </SHTableCell>
    );

    const renderScoreCells = (row: FeatureAnalysisRowDTO) => {
      const { analysisData } = row;

      return analysisData?.map((data, index) => (
        <SHTableCell
          key={index}
          className="SummaryAnalysisTableCell SummaryAnalysisTableCell-TotalScore"
          sx={{ paddingX: "10px !important", paddingY: "0px !important" }}
        >
          <SummaryAnalysisTableTotalScoreCell analysisData={data} />
        </SHTableCell>
      ));
    };

    const renderFeatureDataCells = (row: FeatureAnalysisRowDTO) => {
      const { analysisData, id } = row;

      return analysisData?.map((data, index) => (
        <SHTableCell
          key={`FeatureData-${id}-${index}`}
          className="SummaryAnalysisTableCell SummaryAnalysisTableCell-Data"
          sx={{ paddingX: "10px !important", paddingY: "0px !important" }}
        >
          <SummaryAnalysisTableDataValueCell
            key={`RenderedFeatureData-${id}-${index}`}
            analysisData={data}
          />
        </SHTableCell>
      ));
    };

    const renderRows = (
      row: FeatureAnalysisRowDTO,
      rowIndex: number,
    ): JSX.Element | null => {
      const { level, subRows } = row;
      const isFeatureLevel = level === FeatureAnalysisRowLevel.Feature;
      const isSubSectionLevel = level === FeatureAnalysisRowLevel.SubSection;

      if (removeSubSectionLevelRows && isSubSectionLevel) return null;
      if (removeFeatureLevelRows && isFeatureLevel) return null;

      return (
        <Fragment key={`Fragment-${row.id}-${rowIndex}`}>
          <SHTableRow
            key={row.id}
            className={`SummaryAnalysisTableRow SummaryAnalysisTableRow-${level}`}
          >
            <SHTableCell
              key={`TitleCell-${row.id}`}
              className={`FeatureAnalysisTitleCell-${level} SummaryAnalysisTableCell SummaryAnalysisTableCell-Title`}
              sx={{ paddingX: "10px !important", paddingY: "0px !important" }}
            >
              <RenderedProductRowTitleCell
                key={`RenderedTitleCell-${row.id}`}
                rowData={row}
              />
            </SHTableCell>

            {renderTotalSelectionsCell(row, isFeatureLevel)}

            {!isFeatureLevel && renderScoreCells(row)}
            {isFeatureLevel && renderFeatureDataCells(row)}
          </SHTableRow>

          {subRows?.map(renderRows)}
        </Fragment>
      );
    };

    return <SHTableBody>{rows?.map(renderRows)}</SHTableBody>;
  },
);
